import React from "react";
import { Box, HStack, Text, VStack } from "native-base";

// Web version of the app's dish details
// (PiattoRNMobileApp screens/menu-item/DishAccessibilityInfo.tsx), driven by the
// dish's `accessibility` field. Labels match the app.
//
// Allergens: the app only shows warnings matched to the signed-in user's
// allergies. The public web page shows what the dish declares instead
// ("Contains" / "May contain"), following the app's safety rules: never a
// "free from" claim, and AI data is labelled as an estimate.
//
// Dietary flags ("Gluten Free", "Vegetarian", "Vegan"…) are effectively
// free-from claims, so they are only shown when the data is NOT AI-generated.
// Seen live: AI marked a ham omelette "Vegetarian, Gluten Free" while also
// listing "may contain Wheat / Gluten".

export const ALLERGEN_NAMES = {
  peanuts: "Peanuts",
  tree_nuts: "Tree nuts",
  milk: "Milk / Dairy",
  eggs: "Eggs",
  fish: "Fish",
  crustaceans: "Crustaceans",
  soy: "Soy",
  wheat_gluten: "Wheat / Gluten",
  sesame: "Sesame",
  lupin: "Lupin",
  molluscs: "Molluscs",
  sulphites: "Sulphites",
  celery: "Celery",
  mustard: "Mustard",
};

export const allergenName = (code) =>
  ALLERGEN_NAMES[code] || (code ? code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, " ") : "");

const SPICE_LABELS = { 0: "None", 1: "Mild", 2: "Medium", 3: "Medium-Hot", 4: "Hot", 5: "Very Hot" };
const TEMPERATURE_LABELS = {
  cold: "Served Cold",
  room: "Room Temperature",
  warm: "Served Warm",
  hot: "Served Hot",
};
const formatFlag = (flag) => String(flag).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const capitalise = (t) => String(t).charAt(0).toUpperCase() + String(t).slice(1);

export const isAiSource = (source) => source === "ai_generated";

const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

/** Dietary flags safe to display: none from AI-generated data. */
export const displayableDietaryFlags = (a) => (a && !isAiSource(a.source) ? list(a.dietaryFlags) : []);

/** True when there is anything to show (mirrors the app's checks). */
export function hasDishAccessibility(a) {
  if (!a || a.dataStatus === "none") return false;
  const m = a.modifications || {};
  return (
    list(a.allergens?.contains).length > 0 ||
    list(a.allergens?.mayContain).length > 0 ||
    (a.spiceLevel != null && a.spiceLevel > 0) ||
    a.temperature != null ||
    list(a.textures).length > 0 ||
    !!(a.sensoryDescription && a.sensoryDescription.trim()) ||
    displayableDietaryFlags(a).length > 0 ||
    !!(m.canRemoveIngredients || m.canSubstitute || m.glutenFreeOption || m.dairyFreeOption || m.veganOption || m.notes)
  );
}

function Row({ label, children }) {
  return (
    <HStack space={2} alignItems="flex-start" flexWrap="wrap">
      <Text fontSize="sm" fontWeight="semibold" color="coolGray.800" minW="110px">
        {label}
      </Text>
      <Text fontSize="sm" color="coolGray.700" flex={1}>
        {children}
      </Text>
    </HStack>
  );
}

export function DishAccessibilityInfo({ accessibility: a }) {
  if (!hasDishAccessibility(a)) return null;
  const contains = list(a.allergens?.contains).map(allergenName);
  const mayContain = list(a.allergens?.mayContain).map(allergenName);
  const m = a.modifications || {};
  const mods = [
    m.canRemoveIngredients && "Can remove ingredients",
    m.canSubstitute && "Substitutions available",
    m.glutenFreeOption && "Gluten-free option available",
    m.dairyFreeOption && "Dairy-free option available",
    m.veganOption && "Vegan option available",
  ].filter(Boolean);

  return (
    <section aria-label="Dish details">
      <VStack space={2} mb={4}>
        <Text fontWeight="semibold" color="coolGray.700">
          Dish details
        </Text>

        {isAiSource(a.source) && (
          <Box bg="amber.50" borderRadius="md" px={2} py={1}>
            <Text fontSize="xs" color="amber.800">
              AI estimate — confirm with venue
            </Text>
          </Box>
        )}

        {contains.length > 0 && <Row label="Contains">{contains.join(", ")}</Row>}
        {mayContain.length > 0 && <Row label="May contain">{mayContain.join(", ")}</Row>}
        {a.spiceLevel != null && a.spiceLevel > 0 && (
          <Row label="Spice level">{`${SPICE_LABELS[a.spiceLevel] || a.spiceLevel} (${a.spiceLevel}/5)`}</Row>
        )}
        {a.temperature && TEMPERATURE_LABELS[a.temperature] && (
          <Row label="Temperature">{TEMPERATURE_LABELS[a.temperature]}</Row>
        )}
        {list(a.textures).length > 0 && <Row label="Textures">{list(a.textures).map(capitalise).join(", ")}</Row>}
        {a.sensoryDescription && a.sensoryDescription.trim() && (
          <Row label="Sensory">{`“${a.sensoryDescription.trim()}”`}</Row>
        )}
        {displayableDietaryFlags(a).length > 0 && (
          <Row label="Dietary">{displayableDietaryFlags(a).map(formatFlag).join(", ")}</Row>
        )}

        {(mods.length > 0 || m.notes) && (
          <VStack space={0.5}>
            <Text fontSize="sm" fontWeight="semibold" color="coolGray.800">
              Modifications Available
            </Text>
            {mods.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {mods.map((t) => (
                  <li key={t} style={{ fontSize: 14, color: "#374151" }}>{t}</li>
                ))}
              </ul>
            )}
            {m.notes ? (
              <Text fontSize="sm" color="coolGray.700">{`Chef's note: ${m.notes}`}</Text>
            ) : null}
          </VStack>
        )}

        <Text fontSize="xs" color="coolGray.600">
          Allergen information may be incomplete. Always tell staff about allergies before ordering.
        </Text>
      </VStack>
    </section>
  );
}

export default DishAccessibilityInfo;
